import { AdminMessage } from "@components/admin/admin-helpers"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Button } from "@components/ui/Button"
import { tv } from "@utils/tw"
import { defineBlock, EditableText } from "eddev/blocks"
import { Link } from "eddev/routing"

export const meta: BlockMeta = {
  title: "Hero Card",
  icon: "id",
  category: "media",
  description: "A hero card with an image, title, and description",
  tags: ["#subpage"],
  flags: {
    fullWidth: true,
    marginRule: "large-card",
  },
  blockStyles: [
    {
      name: "small",
      label: "Small",
      isDefault: true,
    },
    {
      name: "large",
      label: "Large",
    },
    {
      name: "flipped",
      label: "Large Flipped",
    },
  ],
}

export default defineBlock("content/hero-card", (props) => {
  const styles = createStyles({
    flipped: props.blockStyle === "flipped",
    size: props.blockStyle === "small" ? "small" : "large",
  })

  const Wrap = props.link?.url ? Link : "div"
  const wrapProps = props.link?.url
    ? {
        href: props.link?.url,
        target: props.link?.target,
      }
    : {}

  return (
    <div data-mixpanel-block="Hero Card">
      <div className="grid-auto">
        <div className={styles.base()}>
          <div className={styles.container()}>
            {/* Image */}
            <div className={styles.imageContainer()}>
              {props.image ? (
                <>
                  <ResponsiveImage {...props.image} objectFit="cover" className={styles.image()} />
                  {props.link?.url && (
                    <div className={styles.imageButtons()}>
                      <Button href={props.link?.url} target={props.link?.target}>
                        {props.link?.title || "Read More"}
                      </Button>
                    </div>
                  )}
                </>
              ) : env.admin ? (
                <div className="absolute inset-0 place-self-center">
                  <AdminMessage>No image has been uploaded</AdminMessage>
                </div>
              ) : null}
            </div>

            {/* Info */}
            <Wrap {...wrapProps} className={styles.info()}>
              <EditableText as="h3" store="title" className={styles.title()} placeholder="Enter a title" />
              <EditableText
                as="p"
                store="description"
                className={styles.description()}
                placeholder="Enter a description"
              />
            </Wrap>
          </div>
        </div>
      </div>
    </div>
  )
})

const createStyles = tv({
  base: "@container",
  slots: {
    container: "flex flex-col bg-bg-low group",
    imageContainer: "flex-none bg-bg-mid relative min-h-[100px]",
    imageButtons:
      "absolute left-0 top-0 w-full h-full flex justify-center items-center transition-opacity duration-300 opacity-0 group-hover:opacity-100",
    image: "object",
    info: "flex flex-col p-6 @3xl:flex-1",
    title: "type-title-m nice-heading @3xl:type-title-l mb-6 flex-grow",
    description: "type-body-m",
  },
  variants: {
    size: {
      small: {
        base: "col-subpage-content-grid",
        imageContainer: "flex-none bg-bg-mid relative min-h-[100px]",
        image: "",
        info: "flex flex-col p-6 !order-1",
      },
      large: {
        base: "col-span-12 md:col-1-10",
        container: "@3xl:flex-row",
        imageContainer: "@3xl:w-[59%] @3xl:min-h-cols-4",
        image: "@3xl:absolute @3xl:inset-0 @3xl:aspect-auto",
        info: "@3xl:flex-1",
      },
    },
    flipped: {
      false: {
        info: "@3xl:-order-1",
      },
    },
  },
})
